import {Player} from "../../model/broadcast/player";

/**
 * Collapse a team's pitcher list to one row per pitcher, for display only.
 *
 * WBSC's boxscore keys are `[side][section][slot]`, and a pitcher can legitimately
 * appear in two different sections:
 *
 *   section 8 — non-batting defensive players (the softball FLEX). Carries `POS: 'P'`
 *               and *batting* stats only; no pitching stats at all.
 *   section 9 — the pitching stat lines. Carries PITCHIP / PITCHES / STRIKES / ...
 *
 * ball-score-server's parser splits the boxscore into "section 0 is the lineup,
 * everything else is a pitcher", so both sections land in `Team.pitchers` and the
 * same pitcher is listed twice. Both rows then show *identical* stats because the
 * section-8 entry is the same object the parser pushed into `Team.roster`, and the
 * later section-9 merge fills that object's pitching stats in behind it.
 *
 * This is a softball problem in practice: with the pitcher batting there is no
 * FLEX, so section 8 does not exist and baseball games usually have no duplicate.
 * It is not a softball problem in principle, hence no sport check here.
 *
 * Deliberately fixed at the display layer rather than in the parser: `Team.pitchers`
 * also feeds the on-air graphics and the companion API, and softball's designated
 * player plus the speed-up rules (a pinch runner for the catcher with two outs, and
 * so on) make re-entry legal in ways a parser change could easily get wrong for
 * those consumers. Dropping a duplicate row on the way to the screen cannot.
 *
 * Section 8 also holds entries that are not pitchers at all, which the same parser
 * rule sweeps in: a non-batting fielder (prod game 206967 lists NOREEN Mike, a
 * second baseman, with no pitching stats) and WBSC's `NO FLEX` placeholder, whose
 * player id is -2 — the same negative-id sentinel convention undecided bracket
 * teams use. Both were being drawn as pitchers, so they are filtered out here too.
 *
 * Order is the order each pitcher first appears, which is what the offense grid
 * relies on to place its pitching-change boundaries — with the duplicates present
 * a one-pitcher game had `pitchers.length === 2` and drew a boundary line for a
 * change that never happened.
 */
export function pitcherRows(pitchers: Player[] | undefined | null): Player[] {
  if (!pitchers?.length) return [];

  const kept = new Map<string, Player>();
  const order: string[] = [];

  pitchers.forEach((pitcher, index) => {
    if (!pitcher) return;
    if (!isPitcher(pitcher)) return;
    const key = identity(pitcher, index);
    const existing = kept.get(key);
    if (!existing) {
      kept.set(key, pitcher);
      order.push(key);
      return;
    }
    // Same pitcher twice. Keep whichever row actually carries his work: the
    // section-8 copy and the section-9 line are identical once merged, but a
    // re-entering pitcher also gets an all-zero stub row we must not prefer.
    if (workload(pitcher) > workload(existing)) kept.set(key, pitcher);
  });

  return order.map(key => kept.get(key)!);
}

/**
 * Whether this row belongs in a pitching panel at all.
 *
 * Rejects WBSC's empty-slot sentinels, which arrive in three different shapes, and
 * rejects a fielder who never pitched: a real pitcher's row is either a section-9
 * stat line — which the parser always stamps with position 1 — or a section-8 entry
 * with `POS: 'P'`, also position 1. A section-8 fielder keeps his own position, so
 * "position is not 1 and no pitching stats recorded" is nobody who has pitched.
 *
 * Deliberately not "has no pitching stats" on its own: a pitcher who has just come
 * in has none yet, and must still be listed.
 */
function isPitcher(pitcher: Player): boolean {
  // Recorded work settles it, whoever the row belongs to and whatever position he
  // is standing at now — a fielder with an innings-pitched line did pitch.
  if (hasPitchingStats(pitcher)) return true;

  // Otherwise the row has to be a real player who has only just come in. WBSC's
  // empty-slot sentinels turn up in three shapes across production data, and the
  // one thing they share is not having a real player id: -2 (game 206967), no id
  // at all (206971), and id '0' with a blank name (206964). All three are stamped
  // position 1 by the parser, so testing the position alone lets them through.
  if (!hasRealPlayerId(pitcher)) return false;

  return pitcher.position === 1;
}

/**
 * Only rejects what it can show is a placeholder: a missing/blank id, or a numeric
 * id that is not positive. An id that is not a number at all is taken at face value
 * — WBSC's are numeric today, and guessing otherwise would hide a real pitcher who
 * has not recorded a stat yet.
 */
function hasRealPlayerId(pitcher: Player): boolean {
  const raw = pitcher.guid;
  if (raw == null || String(raw).trim() === '') return false;
  const id = Number(raw);
  return Number.isFinite(id) ? id > 0 : true;
}

function hasPitchingStats(pitcher: Player): boolean {
  const p = pitcher.gameStats?.pitching;
  return !!p && Object.keys(p).length > 0;
}

/**
 * `guid` is WBSC's player id and is what identifies a pitcher. Falling back to the
 * name covers a row that somehow lost its id; falling back to the index keeps two
 * anonymous rows apart rather than silently merging two different people.
 */
function identity(pitcher: Player, index: number): string {
  if (pitcher.guid) return `guid:${pitcher.guid}`;
  if (pitcher.name) return `name:${pitcher.name.trim().toLowerCase()}`;
  return `index:${index}`;
}

/**
 * How much pitching a row actually records. Only used to choose between rows for
 * the same pitcher, so the scale does not matter — only the ordering does.
 */
function workload(pitcher: Player): number {
  const p = pitcher.gameStats?.pitching;
  if (!p) return 0;
  return (p.pitches ?? 0) * 1000
    + outsFromIp(p.ip) * 10
    + (p.bfp ?? 0) + (p.h ?? 0) + (p.bb ?? 0) + (p.k ?? 0);
}

/** Innings pitched are recorded as `4.2` meaning four innings and two outs. */
function outsFromIp(ip: number | undefined): number {
  if (ip == null) return 0;
  const whole = Math.floor(ip);
  const outs = Math.round((ip - whole) * 10);
  return whole * 3 + (outs >= 1 && outs <= 2 ? outs : 0);
}
